import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Link,
  TextField,
  Typography,
} from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import { useTheme } from "../../../context/ThemeContext";
import envConfig from "../../../config";
import { enqueueSnackbar } from "notistack";
import { default_headers } from "../../../utils/api";
import { getCookie, setCrossDomainCookie } from "../../../utils/cookie-utils";
import {
  authxAppsBase,
  getLoginVerificationRedirect,
  getPendingVerificationPath,
  isAuthxLoginSessionUsable,
  loginAuthxHeadersPreSession,
  normalizeAuthxSession,
  persistAuthxSessionCookies,
  persistPendingVerificationState,
  clearPendingVerificationState,
} from "../../../utils/authx-login-helpers";
import ThemeToggle from "../../ThemeToggle/ThemeToggle";

const bootstrapHeaders = {
  ...default_headers,
  "olivs-api-real-ip": "115.70.37.103",
  "olivs-api-computer-name": "BTM-WS2019",
};

const RegisterSuccessPanel = () => {
  const { isDarkMode } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const [updatingEmail, setUpdatingEmail] = useState(false);
  const [login, setLogin] = useState("");
  const [email, setEmail] = useState("");
  const [newEmail, setNewEmail] = useState("");
  const [loginSecured, setLoginSecured] = useState("");
  const [phone, setPhone] = useState("");
  const [emailResent, setEmailResent] = useState(false);
  const [loadingResend, setLoadingResend] = useState(false);
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [updatingEmailLoading, setUpdatingEmailLoading] = useState(false);

  const handleVerification = async () => {
    setLoading(true);
    const url = `https://${envConfig.apiDev1}/api/v3/en-au/users/verify-login-email-by-code-async?BaseHostURL=${envConfig.loginUrl}`;
    const body = {
      LoginSecuredHash: loginSecured,
      VerificationCode: code,
    };

    try {
      const response = await fetch(url, {
        method: "POST",
        headers: bootstrapHeaders,
        body: JSON.stringify(body),
      });

      const data = await response.json();
      if (data.SuccessYN === "Y") {
        const generateTokenData = JSON.stringify({
          loginHash: loginSecured,
        });

        const generateTokenResponse = await fetch(`${authxAppsBase()}/login`, {
          method: "POST",
          headers: loginAuthxHeadersPreSession("115.70.37.103"),
          body: generateTokenData,
        });

        const generateTokenJson = await generateTokenResponse.json();
        const sess = normalizeAuthxSession(generateTokenJson);
        const verificationRedirect = getLoginVerificationRedirect(sess);

        if (
          isAuthxLoginSessionUsable(
            generateTokenResponse.ok,
            sess,
            verificationRedirect,
          )
        ) {
          persistAuthxSessionCookies(generateTokenJson, email);
          setCrossDomainCookie("loginSecuredHash", loginSecured);

          if (verificationRedirect) {
            persistPendingVerificationState(sess, loginSecured);
          } else {
            clearPendingVerificationState();
          }

          if (verificationRedirect === "email") {
            navigate("/verify-email", {
              state: {
                firstName: sess.firstName,
                email,
                loginSecured,
                accessKey: sess.accessKey,
                refreshKey: sess.refreshKey,
              },
            });
          } else if (verificationRedirect === "phone") {
            navigate("/verify-phone", {
              state: {
                loginSecuredHash: loginSecured,
                accessKey: sess.accessKey,
                refreshKey: sess.refreshKey,
                ...(phone ? { phone } : sess.mobPhoneNo ? { phone: sess.mobPhoneNo } : {}),
              },
            });
          } else {
            navigate("/mfa", {
              state: {
                email,
                accessKey: sess.accessKey,
                refreshKey: sess.refreshKey,
                ip: "115.70.37.103",
                loginSecuredHash: loginSecured,
              },
            });
          }
        }
      } else if (data?.SuccessYN === "N") {
        setCode("");
      }
    } catch (error) {
      console.error(error);
      setCode("");
    } finally {
      setLoading(false);
    }
  };

  const silentlySendVerificationEmail = async () => {
    if (!loginSecured) return;

    const data = JSON.stringify({
      LoginSecuredHash: loginSecured,
    });

    try {
      await fetch(
        `https://${envConfig.apiDev1}/api/v3/en-au/users/send-verification-email?basehosturl=${envConfig.loginUrl}`,
        {
          method: "POST",
          headers: bootstrapHeaders,
          body: data,
        }
      );
    } catch (error) {
      console.error("Error silently sending verification email:", error);
    }
  };

  useEffect(() => {
    const stateEmail = location.state?.email;
    const stateLoginSecured =
      location.state?.loginSecured ?? location.state?.loginSecuredHash;
    const hasState = Boolean(stateEmail && stateLoginSecured);
    const hasLocal = Boolean(email && loginSecured);

    if (!hasState && !hasLocal) {
      const cookieEmail = getCookie("loginEmail");
      const cookieLoginSecured = getCookie("loginSecuredHash");
      const cookieFirstName = getCookie("firstName");

      if (
        cookieEmail &&
        cookieLoginSecured &&
        getPendingVerificationPath() === "/verify-email"
      ) {
        setEmail(cookieEmail);
        setLoginSecured(cookieLoginSecured);
        setLogin(cookieFirstName || cookieEmail.split("@")[0] || "");
        return;
      }

      navigate("/register");
      return;
    }

    if (!email || !loginSecured) {
      setEmail(stateEmail || email);
      setLoginSecured(stateLoginSecured || loginSecured);
      setLogin(
        location.state?.firstName ||
          stateEmail?.split("@")?.[0] ||
          email?.split("@")?.[0] ||
          "",
      );
    }
    if (location.state?.phone) {
      setPhone(location.state.phone);
    }
  }, [location.state, login, email, loginSecured, navigate]);

  useEffect(() => {
    if (loginSecured) {
      silentlySendVerificationEmail();
    }
  }, [loginSecured]);

  const handleResendEmail = async () => {
    if (loadingResend) return;
    setLoadingResend(true);
    const data = JSON.stringify({
      LoginSecuredHash: loginSecured,
    });

    try {
      const response = await fetch(
        `https://${envConfig.apiDev1}/api/v3/en-au/users/send-verification-email?basehosturl=${envConfig.loginUrl}`,
        {
          method: "POST",
          headers: bootstrapHeaders,
          body: data,
        }
      );

      const responseData = await response.json();
      if (responseData.EmailSentYN === "Y") {
        enqueueSnackbar("Please check your email shortly", {
          variant: "success",
        });
        setEmailResent(true);
      }
      setLoadingResend(false);
    } catch (error) {
      setLoadingResend(false);
      console.error("Error:", error);
    }
  };

  const handleUpdateEmail = async () => {
    if (updatingEmailLoading) return;

    const url = `https://${envConfig.apiDev1}/api/v3/en-au/users/change-login-email?BaseHostURL=${envConfig.loginUrl}`;

    const data = JSON.stringify({
      LoginSecuredHash: loginSecured,
      NewLoginEmail: newEmail,
    });

    setUpdatingEmailLoading(true);
    try {
      const response = await fetch(url, {
        method: "POST",
        headers: bootstrapHeaders,
        body: data,
      });

      const responseData = await response.json();
      if (responseData.SuccessYN === "Y") {
        setUpdatingEmail(false);
        setEmail(newEmail);
        setLoginSecured(responseData.NewLoginSecuredHash || "");
      }
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setUpdatingEmailLoading(false);
    }
  };

  return (
    <Box className="glass-card" sx={{ p: { xs: 3.5, md: 4 }, width: "100%" }}>
      <Typography variant="h2" sx={{ fontWeight: 700, mb: 1, color: "text.primary" }}>
        Verify your e-mail
      </Typography>
      <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
        Welcome, {login}!
      </Typography>
      <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
        Let&apos;s verify your e-mail: <Box component="span" sx={{ fontWeight: 600, color: "text.primary" }}>{email}</Box>
      </Typography>
      <Typography variant="body2" sx={{ color: "text.secondary", mb: 3 }}>
        Please check your inbox, we sent you a message with a code. Enter the code below:
      </Typography>

      <TextField
        type="tel"
        fullWidth
        value={code}
        onChange={(e) => {
          const newValue = e.target.value;
          if (/^\d*$/.test(newValue) && newValue.length <= 6) {
            setCode(newValue);
          }
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter" && !loading) {
            handleVerification();
          }
        }}
        inputProps={{
          style: {
            textAlign: "center",
            fontSize: "16px",
            letterSpacing: "5px",
          },
        }}
        sx={{
          mb: 3,
          "input::-webkit-outer-spin-button, input::-webkit-inner-spin-button": {
            WebkitAppearance: "none",
            margin: 0,
          },
          "input[type=number]": {
            MozAppearance: "textfield",
          },
        }}
        data-testid="register-success--TextField-0"
      />

      <Button
        variant="outlined"
        className="btn-glass"
        fullWidth
        disabled={loading || code.length !== 6}
        onClick={handleVerification}
        sx={{ mb: 2 }}
        data-testid="register-success--Button-0"
      >
        {loading ? <CircularProgress size={24} color="inherit" /> : "Verify"}
      </Button>

      {!loginSecured ? null : !updatingEmail ? (
        <Box sx={{ mb: 2 }}>
          <Link
            component="button"
            type="button"
            className="link"
            onClick={() => setUpdatingEmail(true)}
            sx={{
              color: "primary.main",
              cursor: "pointer",
              fontSize: "14px",
              fontWeight: 600,
              textDecoration: "none",
              background: "none",
              border: "none",
              p: 0,
            }}
            data-testid="register-success--Link-0"
          >
            Edit E-mail Address
          </Link>
        </Box>
      ) : (
        <Box sx={{ display: "flex", gap: 1, mb: 2, alignItems: "flex-start", flexWrap: "wrap" }}>
          <TextField
            label="Email Address"
            inputProps={{ autoComplete: "off" }}
            required
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
            sx={{ flex: 1, minWidth: 180 }}
            data-testid="register-success--TextField-1"
          />
          <Button
            onClick={handleUpdateEmail}
            variant="outlined"
            disabled={updatingEmailLoading}
            data-testid="register-success--Button-1"
          >
            Update
          </Button>
          <Button
            onClick={() => setUpdatingEmail(false)}
            variant="outlined"
            disabled={updatingEmailLoading}
            data-testid="register-success--Button-2"
          >
            Cancel
          </Button>
        </Box>
      )}

      <Typography variant="body2" sx={{ color: "text.secondary", mb: 3 }}>
        Can&apos;t find the message? Check your spam folder, and add our address:{" "}
        <Link
          href="mailto:sender@app.exacc.au"
          sx={{ color: "primary.main", fontWeight: 600 }}
          data-testid="register-success--a-0"
        >
          sender@app.exacc.au
        </Link>{" "}
        to your safe list. We will only send important notifications from this e-mail.
      </Typography>

      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", mb: 3 }}>
        {emailResent ? (
          <Typography variant="body2" color="text.secondary">
            Please check your email shortly.
          </Typography>
        ) : (
          <Link
            component="button"
            type="button"
            onClick={handleResendEmail}
            className="link"
            disabled={loadingResend}
            sx={{
              color: loadingResend ? "text.disabled" : "primary.main",
              cursor: loadingResend ? "default" : "pointer",
              fontWeight: 600,
              fontSize: 14,
              textDecoration: "none",
              background: "none",
              border: "none",
              p: 0,
            }}
            data-testid="register-success--Link-1"
          >
            Send E-Mail Again
          </Link>
        )}
      </Box>

      <Box sx={{ display: "flex", justifyContent: "center" }}>
        <ThemeToggle title={isDarkMode ? "Switch to light mode" : "Switch to dark mode"} />
      </Box>
    </Box>
  );
};

export default RegisterSuccessPanel;
